"use client"

import React, { useState, useEffect, useCallback } from "react";
import MasterSideBar from "../../components/MasterSideBar";
import TopBar from "../../components/TopbarNav";
import { updateStaff, getOneStaff } from "../../api/staff-calls-api";
import Select from "react-select";
import countries from "../../utils/countryList";
import {
    FiSave, FiUser, FiBriefcase, FiCreditCard,
    FiBookOpen, FiUsers, FiChevronDown, FiChevronUp, FiLock, FiMapPin, FiSettings, FiMail, FiPhone, FiCalendar, FiGlobe
} from "react-icons/fi";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";

const countryOptions = countries.map((c) => ({ label: `${c.flag} ${c.name}`, value: c.name }));

// --- Collapsible Section Component ---
const Section = ({ title, icon: Icon, children }) => {
    const [isOpen, setIsOpen] = useState(true);
    return (
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden mb-6">
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between px-6 py-4 bg-gray-50/50 hover:bg-gray-50 transition-colors"
            >
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-xl shadow-sm text-orange-600">
                        <Icon size={18} />
                    </div>
                    <h3 className="text-[10px] font-black text-gray-800 uppercase tracking-widest">{title}</h3>
                </div>
                {isOpen ? <FiChevronUp className="text-gray-400" /> : <FiChevronDown className="text-gray-400" />}
            </button>
            {isOpen && (
                <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {children}
                </div>
            )}
        </div>
    );
};

export default function FOSettingsPage() {
    const user = JSON.parse(localStorage.getItem("user"));
    const staffId = user?.id;
    const token = localStorage.getItem("token");

    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState("profile");

    const formatDate = (dateString) => (dateString ? new Date(dateString).toISOString().split("T")[0] : "");

    const fetchStaff = useCallback(async () => {
        try {
            const res = await getOneStaff(staffId);
            const data = res.data || res;

            // Mapping all fields back exactly as in your original logic
            setFormData({
                ...data,
                dateOfBirth: formatDate(data.dateOfBirth),
                villageOrBlock: data.residence?.villageOrBlock || "",
                payam: data.residence?.payam || "",
                county: data.residence?.county || "",
                state: data.residence?.state || "",
                jobTitle: data.jobTitle || "",
                department: data.department || "",
                dateOfEmployment: formatDate(data.dateOfEmployment),
                employmentType: data.employmentType || "",
                contractDuration: data.contractDuration || "",
                salaryRange: data.salaryRange || "",
                bankName: data.bankingDetails?.[0]?.bankName || "",
                bankBranch: data.bankingDetails?.[0]?.bankBranch || "",
                accountName: data.bankingDetails?.[0]?.accountName || "",
                accountNumber: data.bankingDetails?.[0]?.accountNumber || "",
                currency: data.bankingDetails?.[0]?.currency || "",
                swiftCode: data.bankingDetails?.[0]?.swiftCode || "",
                institution: data.qualifications?.[0]?.institution || "",
                award: data.qualifications?.[0]?.award || "",
                yearFrom: data.qualifications?.[0]?.yearFrom || "",
                yearTo: data.qualifications?.[0]?.yearTo || "",
                keyExpertise: data.workHistory?.keyExpertise || "",
                recentEmployer: data.workHistory?.recentEmployer || "",
                workrole: data.workHistory?.workrole || "",
                periodOfService: data.workHistory?.periodOfService || "",
                recentGrossAnnualPaymentRange: data.workHistory?.recentGrossAnnualPaymentRange || "",
                nokName: data.nextOfKin?.name || "",
                nokSex: data.nextOfKin?.sex || "",
                nokNationality: data.nextOfKin?.nationality || "",
                nokRelationship: data.nextOfKin?.relationship || "",
                nokVillageOrBlock: data.nextOfKin?.residence?.villageOrBlock || "",
                nokPayam: data.nextOfKin?.residence?.payam || "",
                nokCounty: data.nextOfKin?.residence?.county || "",
                nokState: data.nextOfKin?.residence?.state || "",
                nokTel: data.nextOfKin?.tel || "",
                nokMobile: data.nextOfKin?.mobile || "",
                nokEmail: data.nextOfKin?.email || "",
            });
        } catch (err) {
            toast.error("Failed to load profile");
        } finally {
            setLoading(false);
        }
    }, [staffId]);

    useEffect(() => { if (staffId) fetchStaff(); }, [fetchStaff]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        const updatedStaff = {
            ...formData,
            residence: {
                villageOrBlock: formData.villageOrBlock,
                payam: formData.payam,
                county: formData.county,
                state: formData.state,
            },
            nextOfKin: {
                name: formData.nokName,
                sex: formData.nokSex,
                nationality: formData.nokNationality,
                relationship: formData.nokRelationship,
                residence: {
                    villageOrBlock: formData.nokVillageOrBlock,
                    payam: formData.nokPayam,
                    county: formData.nokCounty,
                    state: formData.nokState,
                },
                tel: formData.nokTel,
                mobile: formData.nokMobile,
                email: formData.nokEmail,
            },
            bankingDetails: [{
                bankName: formData.bankName,
                bankBranch: formData.bankBranch,
                accountName: formData.accountName,
                accountNumber: formData.accountNumber,
                currency: formData.currency,
                swiftCode: formData.swiftCode,
            }],
            qualifications: [{
                yearFrom: formData.yearFrom,
                yearTo: formData.yearTo,
                institution: formData.institution,
                award: formData.award,
            }],
            workHistory: {
                keyExpertise: formData.keyExpertise,
                recentEmployer: formData.recentEmployer,
                workrole: formData.workrole,
                periodOfService: formData.periodOfService,
                recentGrossAnnualPaymentRange: formData.recentGrossAnnualPaymentRange,
            },
        };

        try {
            await updateStaff(staffId, updatedStaff);
            toast.success("Profile updated successfully");
        } catch (err) {
            toast.error("Failed to update profile");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="flex h-screen bg-gray-50 font-sans overflow-hidden">
            <MasterSideBar
                visible={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
                collapsed={sidebarCollapsed}
                setCollapsed={setSidebarCollapsed}
                onLogout={() => { localStorage.clear(); window.location.href = "/"; }}
            />

            <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0 md:ml-64'}`}>
                <TopBar onSidebarToggle={() => setSidebarOpen(true)} onSearchChange={() => {}} />

                <main className="flex-1 overflow-y-auto p-4 md:p-8 mt-16 lg:mt-20">
                    <div className="max-w-6xl mx-auto">

                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                            <div>
                                <h1 className="text-xl font-bold text-gray-800">Account Settings</h1>
                                <p className="text-sm text-gray-500">Manage your personal information and security</p>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="relative pb-24">
                            {loading ? (
                                <div className="py-20 text-center">
                                    <div className="w-10 h-10 border-4 border-orange-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Loading Personnel Data...</p>
                                </div>
                            ) : (
                                <>
                                    <Section title="Personal Information" icon={FiUser}>
                                        {renderField("First Name", "firstName", "text", null, true)}
                                        {renderField("Last Name", "lastName", "text", null, true)}
                                        {renderField("Email", "email", "email", null, true)}
                                        {renderField("Gender", "gender", "select", ["Male", "Female"], true)}
                                        {renderField("Date of Birth", "dateOfBirth", "date", null, true)}
                                        {renderField("Role", "role", "text", null, true)}
                                        {renderField("Marital Status", "maritalStatus", "select", ["Single", "Married", "Other"])}
                                        <div className="flex flex-col">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Nationality</label>
                                            <Select
                                                options={countryOptions}
                                                value={countryOptions.find((opt) => opt.value === formData.nationality) || null}
                                                onChange={(sel) => setFormData({ ...formData, nationality: sel?.value || "" })}
                                                className="text-sm"
                                            />
                                        </div>
                                        {renderField("National ID / Passport", "nationalIdOrPassport")}
                                        {renderField("Village or Block", "villageOrBlock")}
                                        {renderField("Payam", "payam")}
                                        {renderField("County", "county")}
                                        {renderField("State", "state")}
                                        {renderField("Telephone", "tel")}
                                        {renderField("Mobile", "mobile")}
                                    </Section>

                                    <Section title="Employment Details" icon={FiBriefcase}>
                                        {renderField("Job Title", "jobTitle", "text", null, true)}
                                        {renderField("Department", "department", "text", null, true)}
                                        {renderField("Date of Employment", "dateOfEmployment", "date", null, true)}
                                        {renderField("Employment Type", "employmentType", "text", null, true)}
                                        {renderField("Contract Duration", "contractDuration", "text", null, true)}
                                        {renderField("Salary Range", "salaryRange", "text", null, true)}
                                    </Section>

                                    <Section title="Banking Details" icon={FiCreditCard}>
                                        {renderField("Bank Name", "bankName")}
                                        {renderField("Bank Branch", "bankBranch")}
                                        {renderField("Account Name", "accountName")}
                                        {renderField("Account Number", "accountNumber")}
                                        {renderField("Currency", "currency")}
                                        {renderField("SWIFT Code", "swiftCode")}
                                    </Section>

                                    <Section title="Education & Work History" icon={FiBookOpen}>
                                        {renderField("Institution", "institution")}
                                        {renderField("Award", "award")}
                                        {renderField("Year From", "yearFrom")}
                                        {renderField("Year To", "yearTo")}
                                        <div className="col-span-1 sm:col-span-2 lg:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-6">
                                            {renderField("Key Expertise", "keyExpertise")}
                                            {renderField("Recent Employer", "recentEmployer")}
                                            {renderField("Previous Role", "workrole")}
                                            {renderField("Period of Service", "periodOfService")}
                                            {renderField("Recent Gross Payment", "recentGrossAnnualPaymentRange")}
                                        </div>
                                    </Section>

                                    <Section title="Next of Kin Information" icon={FiUsers}>
                                        {renderField("NOK Name", "nokName")}
                                        {renderField("NOK Sex", "nokSex", "select", ["Male", "Female"])}
                                        <div className="flex flex-col">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">NOK Nationality</label>
                                            <Select
                                                options={countryOptions}
                                                value={countryOptions.find((opt) => opt.value === formData.nokNationality) || null}
                                                onChange={(sel) => setFormData({ ...formData, nokNationality: sel?.value || "" })}
                                                className="text-sm"
                                            />
                                        </div>
                                        {renderField("Relationship", "nokRelationship")}
                                        {renderField("NOK Village/Block", "nokVillageOrBlock")}
                                        {renderField("NOK Payam", "nokPayam")}
                                        {renderField("NOK County", "nokCounty")}
                                        {renderField("NOK State", "nokState")}
                                        {renderField("NOK Telephone", "nokTel")}
                                        {renderField("NOK Mobile", "nokMobile")}
                                        {renderField("NOK Email", "nokEmail", "email")}
                                    </Section>

                                    <div className="fixed bottom-8 right-8 z-40">
                                        <button
                                            type="submit"
                                            disabled={saving}
                                            className="flex items-center gap-3 px-8 py-4 bg-gray-900 text-white rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-2xl hover:bg-orange-600 transition-all disabled:opacity-50"
                                        >
                                            <FiSave size={18} />
                                            {saving ? "Saving..." : "Update Profile"}
                                        </button>
                                    </div>
                                </>
                            )}
                        </form>
                    </div>
                </main>
            </div>

            <ToastContainer />
        </div>
    );

    function renderField(label, name, type = "text", options, readOnly = false) {
        return (
            <div className="flex flex-col">
                <label className="flex items-center gap-2 text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">
                    {label}
                    {readOnly && <FiLock className="text-gray-300" size={10} />}
                </label>
                {type === "select" ? (
                    <select
                        name={name}
                        value={formData[name] || ""}
                        onChange={handleChange}
                        className={`w-full bg-gray-50 border-none rounded-xl p-3 text-sm focus:ring-2 focus:ring-orange-500 ${readOnly ? "opacity-60 cursor-not-allowed" : ""}`}
                        disabled={readOnly}
                    >
                        <option value="">Select...</option>
                        {options.map((opt, i) => <option key={i} value={opt}>{opt}</option>)}
                    </select>
                ) : (
                    <input
                        name={name}
                        type={type}
                        value={formData[name] || ""}
                        onChange={handleChange}
                        className={`w-full border-none rounded-xl p-3 text-sm focus:ring-2 focus:ring-orange-500 ${readOnly ? "bg-gray-100 text-gray-500 cursor-not-allowed" : "bg-gray-50"}`}
                        readOnly={readOnly}
                    />
                )}
            </div>
        );
    }
}